import React, { useState, useEffect } from 'react';
import { obtenerTodosUsuarios, banearUsuario, desbanearUsuario, actualizarRolUsuario, obtenerDatosUsuario, obtenerUsuariosConectados } from '../services/userService';
import UserRoleSection from './UserModeration/UserRoleSection';
import ConfirmationDialog from './UserModeration/ConfirmationDialog';
import UserProfileModal from './UserModeration/UserProfileModal';

const UserModerationPanel = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accionesUsuario, setAccionesUsuario] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [accionPendiente, setAccionPendiente] = useState(null);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [mostrarTarjetaUsuario, setMostrarTarjetaUsuario] = useState(false);
  const [permisoModeracion, setPermisoModeracion] = useState(null); // null = cargando, 'admin' = admin, 'moderador' = moderador, false = no tiene permiso

  // Estados para los usuarios conectados en tiempo real
  const [usuariosConectados, setUsuariosConectados] = useState(new Set());

  useEffect(() => {
    const verificarPermisosInicial = async () => {
      try {
        const response = await obtenerDatosUsuario();
        if (response.success && (response.user.rol === 'admin' || response.user.rol === 'moderador')) {
          setPermisoModeracion(response.user.rol);
          cargarUsuarios();
        } else {
          setPermisoModeracion(false);
          setError('No tienes permisos para acceder a esta sección');
        }
      } catch (err) {
        console.error('Error al verificar permisos:', err);
        setPermisoModeracion(false);
        setError('No estás autenticado o no tienes permisos para acceder a esta sección');
      } finally {
        setLoading(false);
      }
    };

    verificarPermisosInicial();
  }, []);

  useEffect(() => {
    if (permisoModeracion) {
      const fetchUsuariosConectados = async () => {
        try {
          const response = await obtenerUsuariosConectados();
          if (response.success) {
            const conectados = new Set(response.usuarios.map((usuario) => String(usuario.id)));
            setUsuariosConectados(conectados);
          } else {
            console.error('Error en la respuesta de usuarios conectados:', response.message);
          }
        } catch (error) {
          console.error('Error al obtener usuarios conectados:', error);
        }
      };

      // Ejecutar inmediatamente y después refrescar cada 5s para estado casi en tiempo real.
      fetchUsuariosConectados();
      const interval = setInterval(fetchUsuariosConectados, 5000);

      return () => clearInterval(interval);
    }
  }, [permisoModeracion]);

  useEffect(() => {
    if (!permisoModeracion) return undefined;

    // Refresco silencioso para mantener baneos/timeouts/roles actualizados en tiempo real.
    const interval = setInterval(() => {
      cargarUsuarios(true);
    }, 15000);

    return () => clearInterval(interval);
  }, [permisoModeracion]);

  const esBaneoActivo = (usuario) => {
    const baneado = usuario?.baneado === 1 || usuario?.baneado === '1' || usuario?.baneado === true;
    if (!baneado) return false;

    if (!usuario?.fecha_baneo) return true;

    const fechaExpiracion = new Date(usuario.fecha_baneo);
    if (Number.isNaN(fechaExpiracion.getTime())) return true;

    return fechaExpiracion > new Date();
  };


  const cargarUsuarios = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await obtenerTodosUsuarios();
      if (response.success) {
        const agrupados = {};
        response.users.forEach((u) => {
          if (!agrupados[u.rol]) agrupados[u.rol] = [];
          agrupados[u.rol].push(u);
        });
        Object.keys(agrupados).forEach((rol) =>
          agrupados[rol].sort((a, b) => a.username.localeCompare(b.username))
        );
        const sorted = [
          ...(agrupados.admin || []),
          ...(agrupados.moderador || []),
          ...(agrupados.usuario || []),
        ];
        setUsuarios(sorted);
        setFilteredUsuarios(sorted);
        return sorted; // <-- devuelve el array actualizado para uso inmediato
      } else {
        if (!silent) setError(response.message || 'Error al cargar usuarios');
        return null;
      }
    } catch (err) {
      if (!silent) setError('Error de conexión al cargar usuarios');
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Filtrar usuarios según el término de búsqueda
  useEffect(() => {
    if (!searchTerm) {
      setFilteredUsuarios(usuarios);
    } else {
      const filtered = usuarios.filter(usuario =>
        usuario.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.rol.toLowerCase().includes(searchTerm.toLowerCase())
      );
      // Reordenar los resultados filtrados por rol y nombre
      const usuariosAgrupados = {};
      filtered.forEach(usuario => {
        if (!usuariosAgrupados[usuario.rol]) {
          usuariosAgrupados[usuario.rol] = [];
        }
        usuariosAgrupados[usuario.rol].push(usuario);
      });

      Object.keys(usuariosAgrupados).forEach(rol => {
        usuariosAgrupados[rol].sort((a, b) => a.username.localeCompare(b.username));
      });

      const sortedUsuarios = [
        ...usuariosAgrupados.admin || [],
        ...usuariosAgrupados.moderador || [],
        ...usuariosAgrupados.usuario || []
      ];
      setFilteredUsuarios(sortedUsuarios);
    }
  }, [searchTerm, usuarios]);

  const toggleAcciones = (userId) => {
    setAccionesUsuario((prev) => {
      const estabaAbierto = prev[userId] || false;
      if (estabaAbierto) {
        return {};
      }
      return { [userId]: true };
    });
  };

  const setAccionMenu = (userId, abierto) => {
    setAccionesUsuario(abierto ? { [userId]: true } : {});
  };

  const confirmarAccion = (accion, userId, duracion = null, nuevoRol = null) => {
    setAccionPendiente({ accion, userId, duracion, nuevoRol });
    setShowConfirmDialog(true);
  };

  const ejecutarAccion = async () => {
    if (!accionPendiente) return;

    const { accion, userId, duracion, nuevoRol } = accionPendiente;
    setShowConfirmDialog(false);
    setAccionPendiente(null);
    setAccionesUsuario({});

    let response;
    try {
      if (accion === 'banear') {
        response = await banearUsuario(userId, duracion);
      } else if (accion === 'desbanear') {
        response = await desbanearUsuario(userId);
      } else if (accion === 'actualizarRol') {
        response = await actualizarRolUsuario(userId, nuevoRol);
      }
    } catch (err) {
      setError('Error de conexión al ejecutar la acción');
      return;
    }

    if (!response?.success) {
      setError(response?.message || 'No se pudo ejecutar la acción');
      return;
    }

    // Recargar lista y usar el array devuelto (no el state, que aún no actualizó)
    const usuariosActualizados = await cargarUsuarios();
    if (usuariosActualizados && usuarioSeleccionado?.id === userId) {
      const actualizado = usuariosActualizados.find((u) => u.id === userId);
      if (actualizado) setUsuarioSeleccionado(actualizado);
    }
  };

  const handleCancelarAccion = () => {
    setShowConfirmDialog(false);
    setAccionPendiente(null);
 };

  const handleActualizarRol = (userId, nuevoRol) => {
    confirmarAccion('actualizarRol', userId, null, nuevoRol);
  };

  const handleBanearDesdePerfil = (userId, duracionDias = 7) => {
    confirmarAccion('banear', userId, duracionDias);
  };

  const handleDesbanearDesdePerfil = (userId) => {
    confirmarAccion('desbanear', userId);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // El error se muestra inline, no bloquea el panel

  // Agrupar usuarios por rol
  const adminUsuarios = filteredUsuarios.filter(u => u.rol === 'admin');
  const moderadorUsuarios = filteredUsuarios.filter(u => u.rol === 'moderador');
  const usuarioUsuarios = filteredUsuarios.filter(u => u.rol === 'usuario');

  return (
    <div className="w-full max-w-6xl mx-auto bg-white rounded-lg shadow-md p-3 sm:p-4 md:p-6 dark:bg-gray-800 dark:text-white overflow-x-hidden">
      <h3 className="text-xl sm:text-2xl font-bold mb-6 dark:text-white flex items-center gap-2">
        <svg width="22" height="22" fill="none" viewBox="0 0 24 24" className="text-blue-700 dark:text-blue-200">
          <path fill="currentColor" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V20h14v-3.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05C17.16 14.1 20 15.17 20 17.5V20h4v-3.5c0-2.33-4.67-3.5-8-3.5z"></path>
        </svg>
        Panel de Moderación
      </h3>
      
      {/* Error inline (no bloquea el panel) */}
      {error && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 dark:hover:text-red-200 font-bold leading-none">&times;</button>
        </div>
      )}

      {/* Buscador */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Buscar usuarios por nombre, email o rol..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300"
        />
      </div>

      {/* Tabla de usuarios por categoría */}
      <div className="space-y-8">
        {/* Administradores */}
        <UserRoleSection
          titulo="Administradores"
          usuarios={adminUsuarios}
          color="text-red-600"
          usuariosConectados={usuariosConectados}
          permisoModeracion={permisoModeracion}
          toggleAcciones={toggleAcciones}
          accionesUsuario={accionesUsuario}
          setAccionMenu={setAccionMenu}
          handleActualizarRol={handleActualizarRol}
          confirmarAccion={confirmarAccion}
          onUserClick={(usuario) => { setUsuarioSeleccionado(usuario); setMostrarTarjetaUsuario(true); }}
        />

        {/* Moderadores */}
        <UserRoleSection
          titulo="Moderadores"
          usuarios={moderadorUsuarios}
          color="text-orange-600"
          usuariosConectados={usuariosConectados}
          permisoModeracion={permisoModeracion}
          toggleAcciones={toggleAcciones}
          accionesUsuario={accionesUsuario}
          setAccionMenu={setAccionMenu}
          handleActualizarRol={handleActualizarRol}
          confirmarAccion={confirmarAccion}
          onUserClick={(usuario) => { setUsuarioSeleccionado(usuario); setMostrarTarjetaUsuario(true); }}
        />

        {/* Usuarios */}
        <UserRoleSection
          titulo="Usuarios"
          usuarios={usuarioUsuarios}
          color="text-blue-600"
          usuariosConectados={usuariosConectados}
          permisoModeracion={permisoModeracion}
          toggleAcciones={toggleAcciones}
          accionesUsuario={accionesUsuario}
          setAccionMenu={setAccionMenu}
          handleActualizarRol={handleActualizarRol}
          confirmarAccion={confirmarAccion}
          onUserClick={(usuario) => { setUsuarioSeleccionado(usuario); setMostrarTarjetaUsuario(true); }}
        />
      </div>

      {/* Diálogo de confirmación */}
      <ConfirmationDialog
        showConfirmDialog={showConfirmDialog}
        handleCancelarAccion={handleCancelarAccion}
        ejecutarAccion={ejecutarAccion}
        accionPendiente={accionPendiente}
      />
      
      {/* Tarjeta de información del usuario */}
      <UserProfileModal
        mostrarTarjetaUsuario={mostrarTarjetaUsuario}
        usuarioSeleccionado={usuarioSeleccionado}
        setMostrarTarjetaUsuario={setMostrarTarjetaUsuario}
        permisoModeracion={permisoModeracion}
        usuariosConectados={usuariosConectados}
        esBaneoActivo={esBaneoActivo}
        onBanear={handleBanearDesdePerfil}
        onDesbanear={handleDesbanearDesdePerfil}
      />
    </div>
  );
};

export default UserModerationPanel;
